import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import { useState, useCallback, useEffect, useRef, type ChangeEvent, type RefObject } from 'react';
import { useAtom } from 'jotai';
import { msg } from '@lingui/core/macro';
import { i18n } from '@/i18n';
import type { InPageCameraErrorPhase } from '@/components/InPageCamera';
import { cameraMode, fallBackToNativeCamera } from '@/lib/cameraMode';
import { cameraOffInPhoneSettings, cameraOffInPhoneSettingsMessage } from '@/lib/permissions';
import { boundNativePhoto, readPhotoTakenAt } from '@/lib/capturePhoto';
import { getErrorMessage, handleCameraError, handleGeolocationError } from '@/lib/errors';
import { embedExifIntoJpeg } from '@/lib/exifWriter';
import { captureCaptureFailure, photoLog } from '@/lib/photoTelemetry';
import { pendingCaptureAtom, type PendingCapture } from '@/lib/atoms';
import { CAMERA_CONFIG, GEOLOCATION_CONFIG } from '@/config/constants';
import type { IExif, PhotoMetadata, PhotoStep } from '@transpordiamet/shared';

export interface GpsPosition {
  latitude: number;
  longitude: number;
}

/** The user picked an existing image rather than taking a photo. Expected, so not reported. */
class NotFreshPhotoError extends Error {}

/**
 * Set just before the native camera opens, cleared when the input reports back.
 * Android can kill Chrome while the camera app is in front; the File then reaches
 * no page, and a record left by another document is how the next page knows.
 *
 * localStorage, because a killed Chrome restores the tab with empty sessionStorage.
 * The navigation type is no signal: the restored tab reports back_forward and
 * wasDiscarded stays false (Chrome 124, Android 15 emulator).
 */
export const CAPTURE_OPENED_KEY = 'capture-opened';

/** Tells the document that opened the camera apart from the one that replaced it. */
const THIS_DOCUMENT = Math.random().toString(36).slice(2);

type CaptureOpened = { sessionId: string; stepId: string; openedAt: string; documentId: string };

// StrictMode mounts twice. Remembering the stamp keeps the second mount from reporting again.
let loggedLossAt: string | null = null;

function markCaptureOpened(sessionId: string, stepId: string): void {
  try {
    const record: CaptureOpened = { sessionId, stepId, openedAt: new Date().toISOString(), documentId: THIS_DOCUMENT };
    localStorage.setItem(CAPTURE_OPENED_KEY, JSON.stringify(record));
  } catch {
    // Private mode. The photo can still be taken; a lost one just cannot be explained.
  }
}

function readCaptureOpened(): CaptureOpened | null {
  try {
    const raw = localStorage.getItem(CAPTURE_OPENED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CaptureOpened>;
    if (!parsed.stepId || !parsed.openedAt || !parsed.documentId) return null;
    return {
      sessionId: parsed.sessionId ?? '',
      stepId: parsed.stepId,
      openedAt: parsed.openedAt,
      documentId: parsed.documentId,
    };
  } catch {
    return null;
  }
}

export function clearCaptureOpened(): void {
  loggedLossAt = null;
  try {
    localStorage.removeItem(CAPTURE_OPENED_KEY);
  } catch {
    // Same private-mode miss as markCaptureOpened.
  }
}

/**
 * An earlier document opened the camera for this step and never heard back, so the
 * photo is gone. The session id scopes the record, so it cannot outlive the link.
 */
export function cameraOpenLostPhoto(sessionId: string, stepId: string): boolean {
  const opened = readCaptureOpened();
  if (!opened || opened.stepId !== stepId) return false;
  if (sessionId && opened.sessionId !== sessionId) return false;
  return opened.documentId !== THIS_DOCUMENT;
}

/**
 * Only a photo taken just now counts. A capture time must be within
 * MAX_PHOTO_AGE_MS of the photo coming back, which keeps gallery photos out.
 *
 * No capture time is not proof of a gallery pick: on Firefox for Android the
 * camera's photo arrives with no DateTimeOriginal at all, while a gallery
 * photo keeps its own (both tested on a Pixel 3, and lastModified is "now"
 * for either, so it cannot tell them apart). So a JPEG with no capture time
 * passes, and anything else (PNG screenshots, etc.) does not.
 */
async function assertFreshPhoto(file: File, receivedAt: string): Promise<void> {
  const takenAt = await readPhotoTakenAt(file);
  const fresh = takenAt
    ? Math.abs(dayjs(receivedAt).diff(takenAt)) <= CAMERA_CONFIG.MAX_PHOTO_AGE_MS
    : file.type === 'image/jpeg';
  if (!fresh) {
    throw new NotFreshPhotoError(i18n._(msg`Please take a new photo with the camera.`));
  }
}

export interface UseCameraWorkflowReturn {
  // State
  showIllustration: boolean;
  /** A photo is back from the native camera and is being resized / waiting for GPS. */
  processingPhoto: boolean;
  capturedPhoto: Blob | null;
  exifData: IExif | null;
  photoMetadata: PhotoMetadata | null;
  gpsPosition: GpsPosition | null;
  /** The high-accuracy fix failed; waiting on the Wi-Fi/cell one. */
  gpsFallback: boolean;
  error: string | null;
  /** The page was killed while the native camera was open, so the photo is gone. */
  photoLost: boolean;
  /** The in-page camera is open (Android Chrome; see cameraMode). */
  showViewfinder: boolean;
  /** An in-page photo is on the preview screen. It uploads once the user confirms it. */
  awaitingApproval: boolean;

  /** Attach to the hidden <input type="file" capture> that opens the native camera. */
  cameraInputRef: RefObject<HTMLInputElement | null>;

  // Handlers
  handleCameraClick: () => void;
  handleIllustrationOk: () => void;
  handleIllustrationBack: () => void;
  handleCameraInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRetake: () => void;
  dismissError: () => void;
  /** Opens the camera again after a killed page dropped the previous photo. */
  recoverLostPhoto: () => void;
  clearCapturedPhoto: () => void;
  /** Drop the local JPEG once R2 is showing it. The upload closure still holds its own copy. */
  releaseCapturedBytes: () => void;
  /** A photo from the in-page camera. */
  acceptInPagePhoto: (photo: Blob) => void;
  /** The user approved the previewed in-page photo. Starts the upload. */
  confirmPhoto: () => void;
  closeViewfinder: () => void;
  handleViewfinderError: (error: unknown, phase: InPageCameraErrorPhase) => void;
}

export interface UseCameraWorkflowOptions {
  currentStep: PhotoStep | null;
  sessionId?: string;
  stepId?: string;
  /**
   * Called once per photo with its GPS metadata: a native photo as soon as it is
   * resized, an in-page one once the user confirms it on the preview.
   */
  onPhotoReady?: (photo: Blob, metadata: PhotoMetadata) => void;
}

/**
 * Hook to manage the capture workflow: illustration, GPS, the native camera,
 * resizing the photo it returns, and retakes.
 *
 * The native camera is opened with input.click() inside the tap handler that
 * asked for it — browsers only open a file picker with a user gesture, and
 * iOS Safari is the strictest about it.
 */
export function useCameraWorkflow({
  currentStep,
  sessionId = '',
  stepId,
  onPhotoReady,
}: UseCameraWorkflowOptions): UseCameraWorkflowReturn {
  const activeStepId = stepId ?? currentStep?.id ?? '';
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [showIllustration, setShowIllustration] = useState(false);
  const [pendingCapture, setPendingCapture] = useAtom(pendingCaptureAtom);
  const [accepting, setAccepting] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<Blob | null>(null);
  const [exifData, setExifData] = useState<IExif | null>(null);
  const [photoMetadata, setPhotoMetadata] = useState<PhotoMetadata | null>(null);
  const [gpsPosition, setGpsPosition] = useState<GpsPosition | null>(null);
  const [gpsFallback, setGpsFallback] = useState(false);
  const lostOnLoad = cameraOpenLostPhoto(sessionId, activeStepId);
  const [error, setError] = useState<string | null>(
    lostOnLoad ? i18n._(msg`The photo did not come back. Take it again.`) : null
  );
  const [photoLost, setPhotoLost] = useState(lostOnLoad);
  const [showViewfinder, setShowViewfinder] = useState(false);
  const [awaitingApproval, setAwaitingApproval] = useState(false);

  const gpsRequestRef = useRef<Promise<void> | null>(null);

  // Latest callback, so a new function on each render does not restart photo processing.
  const onPhotoReadyRef = useRef(onPhotoReady);
  onPhotoReadyRef.current = onPhotoReady;

  /**
   * Acquire the fix for this capture. Never call this in the tap that opens
   * the camera: on Firefox for Android the geolocation permission request
   * lands next to the file prompt and Fenix drops the prompt (logcat:
   * UpdatePermissionsRequest, then ConsumePromptRequestAction ~5ms later), so
   * the camera never opens. One request at a time.
   */
  const requestGpsPosition = useCallback(() => {
    if (!GEOLOCATION_CONFIG.ENABLED || gpsRequestRef.current) return;

    const getPosition = (enableHighAccuracy: boolean) =>
      new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy,
          timeout: GEOLOCATION_CONFIG.TIMEOUT,
          maximumAge: GEOLOCATION_CONFIG.MAX_AGE,
        });
      });

    setGpsFallback(false);
    gpsRequestRef.current = (async () => {
      try {
        const position = await getPosition(GEOLOCATION_CONFIG.ENABLE_HIGH_ACCURACY).catch(
          (err: GeolocationPositionError) => {
            // Indoors or in a garage the GPS chip often gets no fix in time; a Wi-Fi/cell fix still records where the vehicle is.
            // 1 = PERMISSION_DENIED: asking again cannot help.
            if (err.code === 1) throw err;
            setGpsFallback(true);
            return getPosition(false);
          }
        );

        setGpsPosition({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      } catch (err) {
        const appError = handleGeolocationError(err as GeolocationPositionError);
        setError(appError.userMessage);
      } finally {
        gpsRequestRef.current = null;
      }
    })();
  }, []);

  /**
   * Open the camera: the in-page viewfinder where cameraMode allows it, so
   * Chrome never goes to the background, otherwise the native camera app.
   * The native one must run inside a tap handler, and nothing else may prompt
   * in it. The illustration stays up behind either camera; it gives way to the
   * progress screen once a photo is back, so the step page never flashes between.
   */
  const openCamera = useCallback(() => {
    const mode = cameraMode();
    photoLog('info', 'camera opened', { stepId: activeStepId, mode });
    setError(null);
    if (mode === 'inpage') {
      setShowViewfinder(true);
      // No file prompt to collide with, so the fix can start with the stream.
      if (!gpsPosition) requestGpsPosition();
      return;
    }
    markCaptureOpened(sessionId, activeStepId);
    cameraInputRef.current?.click();
  }, [activeStepId, gpsPosition, requestGpsPosition, sessionId]);

  const handleCameraClick = useCallback(() => {
    setError(null);
    setGpsPosition(null);

    // Show the illustration first; its OK button opens the camera. Start the
    // fix now, while the user reads it, so it is usually in by the time the
    // photo comes back.
    if (currentStep?.illustration_url) {
      setShowIllustration(true);
      requestGpsPosition();
      return;
    }

    openCamera();
  }, [currentStep, openCamera, requestGpsPosition]);

  const handleIllustrationOk = useCallback(() => {
    openCamera();
  }, [openCamera]);

  /** Back to the step page. The camera may have been opened and cancelled already. */
  const handleIllustrationBack = useCallback(() => {
    clearCaptureOpened();
    setShowIllustration(false);
    setError(null);
  }, []);

  const pendingPhoto: PendingCapture | null =
    pendingCapture &&
    pendingCapture.stepId === activeStepId &&
    (sessionId === '' || pendingCapture.sessionId === sessionId)
      ? pendingCapture
      : null;

  const handleCameraInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Clear so the next photo fires onChange even for an identical file.
      event.target.value = '';
      if (!file) {
        // Android dropped the file without killing the page. Leave the illustration up.
        // A killed page never gets this event; the next page reads CAPTURE_OPENED_KEY instead.
        clearCaptureOpened();
        captureCaptureFailure('camera closed with no file', { stepId: activeStepId });
        return;
      }
      clearCaptureOpened();
      setPhotoLost(false);
      photoLog('info', 'file received', { stepId: activeStepId });
      setAccepting(true);
      setPendingCapture({
        sessionId,
        stepId: activeStepId,
        file,
        capturedAt: dayjs().toISOString(),
      });
      // No illustration, so no fix was started before the camera opened.
      if (!gpsPosition) requestGpsPosition();
    },
    [activeStepId, gpsPosition, requestGpsPosition, sessionId, setPendingCapture]
  );

  /** The in-page camera's photo goes through the same GPS wait and upload as a native one. */
  const acceptInPagePhoto = useCallback(
    (photo: Blob) => {
      setShowViewfinder(false);
      setPhotoLost(false);
      photoLog('info', 'file received', { stepId: activeStepId, mode: 'inpage' });
      setAccepting(true);
      setPendingCapture({
        sessionId,
        stepId: activeStepId,
        file: new File([photo], 'photo.jpg', { type: 'image/jpeg' }),
        capturedAt: dayjs().toISOString(),
        inPage: true,
      });
      if (!gpsPosition) requestGpsPosition();
    },
    [activeStepId, gpsPosition, requestGpsPosition, sessionId, setPendingCapture]
  );

  const handleViewfinderError = useCallback(
    (err: unknown, phase: InPageCameraErrorPhase) => {
      setShowViewfinder(false);
      if (phase === 'start') {
        // This browser will not stream the camera. Its native camera takes the next photo.
        fallBackToNativeCamera();
        captureCaptureFailure('in-page camera failed to start', {
          stepId: activeStepId,
          error: (err as Error | undefined)?.name ?? '',
        });
        // Refused with the site allowed: the switch is in Chrome's Android settings, so say where.
        void cameraOffInPhoneSettings(err).then((offInPhone) =>
          setError(offInPhone ? cameraOffInPhoneSettingsMessage() : handleCameraError(err).userMessage)
        );
        return;
      }
      console.error('Failed to capture photo:', err);
      Sentry.captureException(err, { tags: { stage: 'photo-capture' } });
      setError(getErrorMessage(err));
    },
    [activeStepId]
  );

  // Android Chrome reports a cancelled camera with `cancel`, not `change`, and React only
  // wires onCancel for <dialog>. Without this the record outlives the cancel.
  useEffect(() => {
    const onCancel = (event: Event) => {
      if (event.target !== cameraInputRef.current) return;
      clearCaptureOpened();
      photoLog('info', 'camera cancelled', { stepId: activeStepId });
    };
    document.addEventListener('cancel', onCancel, true);
    return () => document.removeEventListener('cancel', onCancel, true);
  }, [activeStepId]);

  // The File dies with the killed page. The record survives, so this page can say so.
  useEffect(() => {
    if (!photoLost) return;
    const opened = readCaptureOpened();
    if (!opened || loggedLossAt === opened.openedAt) return;
    loggedLossAt = opened.openedAt;
    captureCaptureFailure('capture lost on page reload', { stepId: activeStepId });
  }, [activeStepId, photoLost]);

  // A remount loses the GPS fix. Ask again so a file kept in the atom can finish.
  useEffect(() => {
    if (pendingPhoto && !gpsPosition) requestGpsPosition();
  }, [pendingPhoto, gpsPosition, requestGpsPosition]);

  // The fix may land after the user is back from the camera app, so
  // processing waits for both.
  useEffect(() => {
    if (!pendingPhoto || (GEOLOCATION_CONFIG.ENABLED && !gpsPosition)) return;
    let cancelled = false;
    setAccepting(true);

    (async () => {
      try {
        // An in-page photo was taken just now and is landscape and bounded already.
        if (!pendingPhoto.inPage) await assertFreshPhoto(pendingPhoto.file, pendingPhoto.capturedAt);
        const bounded = pendingPhoto.inPage ? pendingPhoto.file : await boundNativePhoto(pendingPhoto.file);
        const metadata: PhotoMetadata = { ...gpsPosition, capturedAt: pendingPhoto.capturedAt };
        const { blob, exifData } = await embedExifIntoJpeg(bounded, metadata);
        if (cancelled) return;
        setShowIllustration(false);
        setCapturedPhoto(blob);
        setExifData(exifData);
        setPhotoMetadata(metadata);
        // The native camera app had its own retake/OK screen. The viewfinder did not, so ask here.
        if (pendingPhoto.inPage) {
          photoLog('info', 'preview shown', { stepId: activeStepId });
          setAwaitingApproval(true);
        } else {
          photoLog('info', 'photo ready', { stepId: activeStepId });
          onPhotoReadyRef.current?.(blob, metadata);
        }
      } catch (err) {
        if (!(err instanceof NotFreshPhotoError)) {
          console.error('Failed to process photo:', err);
          Sentry.captureException(err, { tags: { stage: 'photo-capture' } });
        }
        if (!cancelled) {
          setError(getErrorMessage(err));
          setPendingCapture(null);
        }
      } finally {
        if (!cancelled) setAccepting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeStepId, pendingPhoto, gpsPosition, setPendingCapture]);

  const capturedRef = useRef(capturedPhoto);
  const pendingRef = useRef(pendingPhoto);
  capturedRef.current = capturedPhoto;
  pendingRef.current = pendingPhoto;

  useEffect(() => {
    return () => {
      if (pendingRef.current && !capturedRef.current) {
        captureCaptureFailure('file received, then the step unmounted before preview', {
          stepId: pendingRef.current.stepId,
        });
      }
    };
  }, []);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!pendingRef.current) return;
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      const discarded = (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true;
      if (!event.persisted && !discarded && nav?.type !== 'reload') return;
      captureCaptureFailure('capture interrupted by page restore', {
        stepId: pendingRef.current.stepId,
        persisted: event.persisted,
        discarded,
        navigation: nav?.type ?? '',
      });
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const clearCapturedPhoto = useCallback(() => {
    setAwaitingApproval(false);
    setCapturedPhoto(null);
    setExifData(null);
    setPhotoMetadata(null);
    setAccepting(false);
    setPendingCapture(null);
  }, [setPendingCapture]);

  /**
   * Back from the viewfinder. Like a cancelled native camera, the illustration
   * or an errored photo stays. A retake from the preview drops the unconfirmed
   * photo instead, so the user is back on the step page.
   */
  const closeViewfinder = useCallback(() => {
    setShowViewfinder(false);
    if (awaitingApproval) {
      photoLog('info', 'preview dropped', { stepId: activeStepId });
      clearCapturedPhoto();
    }
  }, [activeStepId, awaitingApproval, clearCapturedPhoto]);

  const releaseCapturedBytes = useCallback(() => {
    setCapturedPhoto(null);
  }, []);

  const confirmPhoto = useCallback(() => {
    if (!capturedPhoto || !photoMetadata) return;
    photoLog('info', 'photo confirmed', { stepId: activeStepId });
    setAwaitingApproval(false);
    onPhotoReadyRef.current?.(capturedPhoto, photoMetadata);
  }, [activeStepId, capturedPhoto, photoMetadata]);

  /**
   * Straight back into the camera. The current photo stays in the progress
   * screen behind it until the new one replaces it, so a cancelled retake
   * lands back on it. Keeps gpsPosition for the new photo.
   */
  const handleRetake = useCallback(() => {
    setError(null);
    if (cameraMode() === 'inpage') {
      photoLog('info', 'camera opened', { stepId: activeStepId, mode: 'inpage', retake: true });
      setShowViewfinder(true);
      return;
    }
    markCaptureOpened(sessionId, activeStepId);
    cameraInputRef.current?.click();
  }, [activeStepId, sessionId]);

  /** Closing the error drops a photo that can no longer get a fix. */
  const dismissError = useCallback(() => {
    clearCaptureOpened();
    setPhotoLost(false);
    setError(null);
    setAccepting(false);
    setPendingCapture(null);
  }, [setPendingCapture]);

  const recoverLostPhoto = useCallback(() => {
    setPhotoLost(false);
    openCamera();
  }, [openCamera]);

  const waitingOnFile = Boolean(pendingPhoto) && !capturedPhoto && !error;

  return {
    // State
    showIllustration,
    processingPhoto: accepting || waitingOnFile,
    capturedPhoto,
    exifData,
    photoMetadata,
    gpsPosition,
    gpsFallback,
    error,
    photoLost,
    showViewfinder,
    awaitingApproval,

    cameraInputRef,

    // Handlers
    handleCameraClick,
    handleIllustrationOk,
    handleIllustrationBack,
    handleCameraInputChange,
    handleRetake,
    dismissError,
    recoverLostPhoto,
    clearCapturedPhoto,
    releaseCapturedBytes,
    acceptInPagePhoto,
    confirmPhoto,
    closeViewfinder,
    handleViewfinderError,
  };
}
